/**
 * useContact - React hook for looking up a contact by ID from ContactStateContext
 * Returns derived state (displayName, primaryPhone, etc.) plus an updateContact helper.
 */

import { useMemo } from 'react';
import type { Contact } from './types';
import { ContactLogic } from './logic';
import { useContactState } from './ContactStateContext';

export function useContact(localId: string) {
  const { state, dispatch } = useContactState();

  const contact = useMemo(() => state.byId[localId], [state.byId, localId]);

  const derived = useMemo(() => {
    if (!contact) return null;
    return {
      ...contact,
      isNeighbor: contact.listingCategories.includes('Neighbor'),
      primaryPhone: ContactLogic.getPrimaryPhone(contact),
      formattedPhone: ContactLogic.getFormattedPhone(contact),
      displayName: ContactLogic.displayName(contact),
      vCard: () => ContactLogic.toVCard(contact),
    };
  }, [contact]);

  const updateContact = useMemo(
    () => (fields: Partial<Contact>) => {
      dispatch({
        type: 'UPDATE_CONTACT',
        payload: { localId: localId, fields },
      });
    },
    [dispatch, localId]
  );

  return { contact: derived, updateContact };
}
